import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RouterLink } from '../../utils/linkNav';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Send,
  Cancel,
  EmojiEvents,
  Gavel,
  RequestQuote,
  LocalShipping,
  Star,
  Balance,
  TrendingUp,
} from '@mui/icons-material';
import {
  PageHeader,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TypePill,
  TableEmptyState,
} from '../../components/common/pageKit';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { formatIndianNumber } from '../../utils/currency';
import {
  procurementService,
  RfqDetail,
  RfqComparison,
  RfqStatus,
  RfqSupplierStatus,
  QuotationStatus,
  TechnicalCompliance,
} from '../../services/procurementService';
import AttachmentsPanel from '../../components/common/AttachmentsPanel';

// ---------------------------------------------------------------------------
// The sourcing workbench: RFQ header + lifecycle ribbon, invited suppliers,
// line items, quotations received, and (the hero) a metrics-as-rows /
// vendors-as-columns comparison grid with best-cell highlighting. Two heavy
// dialogs -- Record Quotation and Award -- live at the bottom of the file.
// ---------------------------------------------------------------------------

const FLOW_STEPS: { key: string; label: string }[] = [
  { key: 'DRAFT', label: 'Draft' },
  { key: 'ISSUED', label: 'Issued' },
  { key: 'RESPONSES_IN', label: 'Responses In' },
  { key: 'UNDER_EVALUATION', label: 'Evaluation' },
  { key: 'AWARDED', label: 'Awarded' },
];

const FLOW_INDEX: Partial<Record<RfqStatus, number>> = {
  DRAFT: 0,
  ISSUED: 1,
  RESPONSES_IN: 2,
  UNDER_EVALUATION: 3,
  AWARDED: 4,
};

const STATUS_TONE: Record<RfqStatus, StatusTone> = {
  DRAFT: 'neutral',
  ISSUED: 'info',
  RESPONSES_IN: 'warning',
  UNDER_EVALUATION: 'warning',
  AWARDED: 'success',
  CANCELLED: 'neutral',
  CLOSED: 'neutral',
};

const SUPPLIER_STATUS_TONE: Record<RfqSupplierStatus, StatusTone> = {
  INVITED: 'neutral',
  RESPONDED: 'success',
  DECLINED: 'error',
};

const QUOTATION_STATUS_TONE: Record<QuotationStatus, StatusTone> = {
  SUBMITTED: 'info',
  SHORTLISTED: 'warning',
  REJECTED: 'error',
  AWARDED: 'success',
};

const COMPLIANCE_TONE: Record<TechnicalCompliance, StatusTone> = {
  COMPLIANT: 'success',
  PARTIAL: 'warning',
  NON_COMPLIANT: 'error',
};

const ELIGIBLE_QUOTATION_STATUSES: QuotationStatus[] = ['SUBMITTED', 'SHORTLISTED'];

const MONEY_METRICS = new Set(['price', 'discount', 'freight', 'tax', 'landed']);
const LOWER_BETTER = new Set(['price', 'freight', 'tax', 'landed', 'delivery']);
const HIGHER_BETTER = new Set(['discount', 'quality']);

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');
const fmtDateTime = (v: string | null | undefined) => (v ? new Date(v).toLocaleString('en-IN') : '—');
const todayISO = () => new Date().toISOString().slice(0, 10);

const formatMetricValue = (key: string, value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (MONEY_METRICS.has(key)) return formatIndianNumber(toNum(value));
  if (key === 'delivery') return `${toNum(value)} day${toNum(value) === 1 ? '' : 's'}`;
  if (key === 'quality') return toNum(value).toFixed(1);
  return String(value);
};

/** Quotation IDs tied for "best" on a metric row, or an empty set if the metric isn't ranked. */
const bestSetForMetric = (metric: RfqComparison['metrics'][number]): Set<number> => {
  const lowerIsBetter = LOWER_BETTER.has(metric.key);
  const higherIsBetter = HIGHER_BETTER.has(metric.key);
  if (!lowerIsBetter && !higherIsBetter) return new Set();

  const entries = Object.entries(metric.values)
    .map(([qid, v]) => [Number(qid), v === null || v === undefined || v === '' ? null : Number(v)] as const)
    .filter((e): e is [number, number] => e[1] !== null && Number.isFinite(e[1]));
  if (entries.length === 0) return new Set();

  const target = lowerIsBetter ? Math.min(...entries.map((e) => e[1])) : Math.max(...entries.map((e) => e[1]));
  return new Set(entries.filter(([, v]) => Math.abs(v - target) < 0.005).map(([qid]) => qid));
};

const RfqDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const colors = useControlRoomColors();

  const [rfq, setRfq] = useState<RfqDetail | null>(null);
  const [comparison, setComparison] = useState<RfqComparison | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [awardOpen, setAwardOpen] = useState(false);
  const [awardDefaultQuotationId, setAwardDefaultQuotationId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [{ rfq: rfqData }, { comparison: comparisonData }] = await Promise.all([
        procurementService.getRfq(id),
        procurementService.getRfqComparison(id),
      ]);
      setRfq(rfqData);
      setComparison(comparisonData);
    } catch (err: any) {
      setError(err.message || 'Failed to load RFQ');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !rfq) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/procurement/rfqs')} sx={{ mb: 2 }}>
          Back to RFQs
        </Button>
        <Alert severity="error">{error || 'RFQ not found'}</Alert>
      </Box>
    );
  }

  const isSideState = (['CANCELLED', 'CLOSED'] as RfqStatus[]).includes(rfq.status);
  const flowIndex = FLOW_INDEX[rfq.status] ?? 0;

  const canEdit = rfq.status === 'DRAFT';
  const canIssue = rfq.status === 'DRAFT';
  const canCancel = !(['AWARDED', 'CANCELLED', 'CLOSED'] as RfqStatus[]).includes(rfq.status);
  const canRecordQuotation = (['ISSUED', 'RESPONSES_IN', 'UNDER_EVALUATION'] as RfqStatus[]).includes(rfq.status);
  const eligibleQuotations = rfq.quotations.filter((q) => ELIGIBLE_QUOTATION_STATUSES.includes(q.status));
  const canAward = canRecordQuotation && eligibleQuotations.length > 0;

  const runAction = async (name: string, fn: () => Promise<void>) => {
    setActionLoading(name);
    setActionError(null);
    try {
      await fn();
    } catch (err: any) {
      setActionError(err.message || `Failed to ${name}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleIssue = () =>
    runAction('issue', async () => {
      await procurementService.issueRfq(id!);
      setNotice('RFQ issued to invited suppliers.');
      await load();
    });

  const handleCancel = () =>
    runAction('cancel', async () => {
      await procurementService.cancelRfq(id!);
      setCancelOpen(false);
      setNotice('RFQ cancelled.');
      await load();
    });

  const handleShortlist = (quotationId: number) =>
    runAction(`shortlist-${quotationId}`, async () => {
      await procurementService.shortlistQuotation(quotationId);
      setNotice('Quotation shortlisted.');
      await load();
    });

  const handleReject = (quotationId: number) =>
    runAction(`reject-${quotationId}`, async () => {
      await procurementService.rejectQuotation(quotationId);
      setNotice('Quotation rejected.');
      await load();
    });

  const openAward = (quotationId: number | null) => {
    setAwardDefaultQuotationId(quotationId);
    setAwardOpen(true);
  };

  const columnByQuotationId = new Map((comparison?.columns || []).map((c) => [c.quotationId, c]));
  const vendorNameFor = (quotationId: number | null | undefined) =>
    quotationId ? columnByQuotationId.get(quotationId)?.vendorName || `#${quotationId}` : null;

  const bannerItems = [
    { label: 'Best Commercial', quotationId: comparison?.best.commercial ?? null, icon: <Balance />, color: colors.accent },
    { label: 'Best Delivery', quotationId: comparison?.best.delivery ?? null, icon: <LocalShipping />, color: colors.cyan },
    { label: 'Best Quality', quotationId: comparison?.best.quality ?? null, icon: <Star />, color: colors.purple },
    { label: 'Best Overall', quotationId: comparison?.best.overall ?? null, icon: <TrendingUp />, color: colors.good },
  ];

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/procurement/rfqs')} sx={{ mb: 1 }}>
        Back to RFQs
      </Button>

      <PageHeader
        title={rfq.rfq_number}
        qualifier={rfq.buyer_name ? `· ${rfq.buyer_name}` : undefined}
        subtitle={`Raised ${fmtDate(rfq.rfq_date)}${rfq.response_deadline ? ` · Response deadline ${fmtDate(rfq.response_deadline)}` : ''}`}
        actions={
          <>
            <TypePill label={rfq.rfq_type.replace(/_/g, ' ')} tint={colors.accent} />
            <StatusDot label={rfq.status.replace(/_/g, ' ')} tone={STATUS_TONE[rfq.status]} />
          </>
        }
      />

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Lifecycle ribbon */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        {isSideState ? (
          <>
            <Stepper activeStep={-1} alternativeLabel sx={{ opacity: 0.45, mb: 2 }}>
              {FLOW_STEPS.map((s) => (
                <Step key={s.key}>
                  <StepLabel>{s.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Alert severity={rfq.status === 'CLOSED' ? 'info' : 'warning'} icon={<Cancel />}>
              <Typography sx={{ fontWeight: 700 }}>RFQ {rfq.status.replace(/_/g, ' ')}</Typography>
            </Alert>
          </>
        ) : (
          <Stepper activeStep={flowIndex} alternativeLabel>
            {FLOW_STEPS.map((s, i) => (
              <Step key={s.key} completed={i < flowIndex}>
                <StepLabel>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Paper>

      {/* Workflow actions */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Workflow Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {canEdit && (
            <Button variant="outlined" startIcon={<Edit />} component={RouterLink} to={`/procurement/rfqs/${id}/edit`}>
              Edit
            </Button>
          )}
          {canIssue && (
            <Button variant="contained" startIcon={<Send />} disabled={actionLoading !== null} onClick={handleIssue}>
              {actionLoading === 'issue' ? 'Issuing...' : 'Issue to Suppliers'}
            </Button>
          )}
          {canRecordQuotation && (
            <Button variant="outlined" startIcon={<RequestQuote />} onClick={() => setRecordOpen(true)}>
              Record Quotation
            </Button>
          )}
          {canAward && (
            <Button variant="contained" color="success" startIcon={<EmojiEvents />} onClick={() => openAward(comparison?.best.overall ?? null)}>
              Award
            </Button>
          )}
          {canCancel && (
            <Button variant="outlined" color="error" startIcon={<Cancel />} disabled={actionLoading !== null} onClick={() => setCancelOpen(true)}>
              Cancel RFQ
            </Button>
          )}
          {!canEdit && !canIssue && !canRecordQuotation && !canAward && !canCancel && (
            <Typography variant="body2" color="text.secondary">
              This RFQ is {rfq.status.replace(/_/g, ' ').toLowerCase()} -- no further actions are available.
            </Typography>
          )}
        </Box>
        {rfq.status === 'AWARDED' && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success" icon={<EmojiEvents />}>
              Awarded to <strong>{rfq.awarded_vendor_name || 'the selected vendor'}</strong>
              {rfq.award_justification ? ` — ${rfq.award_justification}` : ''}
            </Alert>
            {rfq.converted_po_id && (
              <Button size="small" sx={{ mt: 1 }} component={RouterLink} to={`/procurement/orders/${rfq.converted_po_id}`}>
                View Purchase Order {rfq.converted_po_number || ''}
              </Button>
            )}
          </Box>
        )}
      </Paper>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              RFQ Details
            </Typography>
            <Grid container spacing={1.5}>
              {[
                ['Type', rfq.rfq_type.replace(/_/g, ' ')],
                ['RFQ Date', fmtDate(rfq.rfq_date)],
                ['Response Deadline', fmtDate(rfq.response_deadline)],
                ['Delivery Location', rfq.delivery_location],
                ['Payment Terms', rfq.payment_terms],
                ['Incoterms', rfq.incoterms],
                ['Source Requisition', rfq.requisition_number],
              ].map(([label, value]) => (
                <Grid size={{ xs: 6 }} key={label}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {value || '—'}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Invited Suppliers
            </Typography>
            {rfq.suppliers.length === 0 ? (
              <Typography variant="body2" color="text.disabled">No suppliers invited yet.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {rfq.suppliers.map((s) => (
                  <Box key={s.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {s.vendor_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.responded_at ? `Responded ${fmtDateTime(s.responded_at)}` : s.invited_at ? `Invited ${fmtDateTime(s.invited_at)}` : ''}
                      </Typography>
                    </Box>
                    <StatusDot label={s.status} tone={SUPPLIER_STATUS_TONE[s.status]} />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Items */}
        <Grid size={{ xs: 12 }}>
          <TableSurface>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Line Items
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Product', 'HSN', 'Qty', 'UOM', 'Required By', 'Specification'].map((h, i) => (
                      <TableCell key={h} align={i === 2 ? 'right' : 'left'} sx={headerCellSx}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rfq.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ border: 0 }}>
                        <TableEmptyState title="No line items on this RFQ" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    rfq.items.map((item) => (
                      <TableRow key={item.id} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1.25 }}>{item.product_name || '—'}</TableCell>
                        <TableCell>{item.hsn_code || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {toNum(item.quantity)}
                        </TableCell>
                        <TableCell>{item.uom || '—'}</TableCell>
                        <TableCell>{fmtDate(item.required_date)}</TableCell>
                        <TableCell>{item.specification || '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Grid>

        {/* Quotations received */}
        <Grid size={{ xs: 12 }}>
          <TableSurface>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Quotations Received
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Vendor', 'Quotation #', 'Total', 'Landed Cost', 'Delivery', 'Quality', 'Status', ''].map((h, i) => (
                      <TableCell key={h || i} align={i === 2 || i === 3 || i === 4 || i === 5 ? 'right' : 'left'} sx={headerCellSx}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rfq.quotations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ border: 0 }}>
                        <TableEmptyState title="No quotations recorded yet" hint="Use Record Quotation once a supplier has quoted." />
                      </TableCell>
                    </TableRow>
                  ) : (
                    rfq.quotations.map((q) => (
                      <TableRow key={q.id} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1.25, fontWeight: 600 }}>{q.vendor_name}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{q.quotation_number}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatIndianNumber(toNum(q.total_amount))}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                          {q.landed_cost !== null ? formatIndianNumber(toNum(q.landed_cost)) : '—'}
                        </TableCell>
                        <TableCell align="right">{q.delivery_days !== null ? `${toNum(q.delivery_days)}d` : '—'}</TableCell>
                        <TableCell align="right">{q.quality_score !== null ? toNum(q.quality_score).toFixed(1) : '—'}</TableCell>
                        <TableCell>
                          <StatusDot label={q.status} tone={QUOTATION_STATUS_TONE[q.status]} />
                        </TableCell>
                        <TableCell align="right">
                          {q.status === 'SUBMITTED' && (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <Button size="small" onClick={() => handleShortlist(q.id)} disabled={actionLoading !== null}>
                                Shortlist
                              </Button>
                              <Button size="small" color="error" onClick={() => handleReject(q.id)} disabled={actionLoading !== null}>
                                Reject
                              </Button>
                            </Box>
                          )}
                          {q.status === 'SHORTLISTED' && (
                            <Button size="small" color="error" onClick={() => handleReject(q.id)} disabled={actionLoading !== null}>
                              Reject
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Grid>

        {/* -------------------------------------------------------------- */}
        {/* Comparison grid -- the hero of the page.                       */}
        {/* -------------------------------------------------------------- */}
        <Grid size={{ xs: 12 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              background: `linear-gradient(160deg, ${colors.accent}0d 0%, transparent 45%)`,
            }}
          >
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Quotation Comparison
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Every submitted or shortlisted quotation, side by side. The best cell on each row is highlighted.
              </Typography>
            </Box>

            {!comparison || comparison.columns.length === 0 ? (
              <Box sx={{ px: 2.5 }}>
                <TableEmptyState
                  title="No quotations to compare yet"
                  hint="Record at least one supplier quotation to see the comparison grid."
                />
              </Box>
            ) : (
              <>
                {/* Best-of banner */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                    gap: 1.5,
                    px: 2.5,
                    pb: 2,
                  }}
                >
                  {bannerItems.map((b) => (
                    <Box
                      key={b.label}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: colors.panel,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          backgroundColor: b.color,
                          color: '#fff',
                          flexShrink: 0,
                          '& svg': { fontSize: 18 },
                        }}
                      >
                        {b.icon}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {b.label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {vendorNameFor(b.quotationId) || '—'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 480 + comparison.columns.length * 160 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...headerCellSx, position: 'sticky', left: 0, backgroundColor: colors.panel, zIndex: 1 }}>
                          Metric
                        </TableCell>
                        {comparison.columns.map((col) => {
                          const score = comparison.scores.find((s) => s.quotationId === col.quotationId);
                          const isOverallBest = comparison.best.overall === col.quotationId;
                          return (
                            <TableCell key={col.quotationId} align="center" sx={{ ...headerCellSx, minWidth: 160 }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {isOverallBest && <EmojiEvents sx={{ fontSize: 14, color: colors.good }} />}
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'none', letterSpacing: 0, color: 'text.primary' }}>
                                    {col.vendorName}
                                  </Typography>
                                </Box>
                                {score && (
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'none' }}>
                                    Score {score.overallScore.toFixed(2)}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comparison.metrics.map((metric) => {
                        const bestSet = bestSetForMetric(metric);
                        return (
                          <TableRow key={metric.key} sx={bodyRowSx}>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                position: 'sticky',
                                left: 0,
                                backgroundColor: colors.panel,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {metric.label}
                            </TableCell>
                            {comparison.columns.map((col) => {
                              const raw = metric.values[col.quotationId];
                              const isBest = bestSet.has(col.quotationId);
                              if (metric.key === 'compliance' && raw) {
                                return (
                                  <TableCell key={col.quotationId} align="center">
                                    <StatusDot
                                      label={String(raw).replace(/_/g, ' ')}
                                      tone={COMPLIANCE_TONE[raw as TechnicalCompliance] || 'neutral'}
                                    />
                                  </TableCell>
                                );
                              }
                              return (
                                <TableCell
                                  key={col.quotationId}
                                  align="center"
                                  sx={{
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: isBest ? 700 : 400,
                                    backgroundColor: isBest ? `${colors.good}1f` : 'transparent',
                                    color: isBest ? colors.good : 'text.primary',
                                    borderLeft: isBest ? `2px solid ${colors.good}` : undefined,
                                  }}
                                >
                                  {formatMetricValue(metric.key, raw)}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                      {canAward && (
                        <TableRow>
                          <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: colors.panel, border: 0 }} />
                          {comparison.columns.map((col) => (
                            <TableCell key={col.quotationId} align="center" sx={{ border: 0, py: 1.5 }}>
                              <Button size="small" variant="outlined" startIcon={<Gavel />} onClick={() => openAward(col.quotationId)}>
                                Award
                              </Button>
                            </TableCell>
                          ))}
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <AttachmentsPanel entityType="rfq" entityId={rfq.id} title="RFQ Documents" />
        </Grid>
      </Grid>

      {/* Cancel confirm */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel RFQ</DialogTitle>
        <DialogContent>
          <DialogContentText>This cannot be undone. Invited suppliers will no longer be able to respond.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Keep It</Button>
          <Button color="error" variant="contained" onClick={handleCancel} disabled={actionLoading === 'cancel'}>
            {actionLoading === 'cancel' ? 'Cancelling...' : 'Cancel RFQ'}
          </Button>
        </DialogActions>
      </Dialog>

      <RecordQuotationDialog
        open={recordOpen}
        rfq={rfq}
        onClose={() => setRecordOpen(false)}
        onCreated={() => {
          setRecordOpen(false);
          setNotice('Quotation recorded.');
          load();
        }}
      />

      <AwardDialog
        open={awardOpen}
        rfq={rfq}
        eligibleQuotations={eligibleQuotations}
        defaultQuotationId={awardDefaultQuotationId}
        onClose={() => setAwardOpen(false)}
        onAwarded={(purchaseOrderId) => {
          setAwardOpen(false);
          if (purchaseOrderId) {
            navigate(`/procurement/orders/${purchaseOrderId}`);
          } else {
            setNotice('RFQ awarded.');
            load();
          }
        }}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Record Quotation dialog
// ---------------------------------------------------------------------------

interface QuotationLineDraft {
  key: string;
  rfqItemId: number;
  productId: string | null;
  itemDescription: string;
  hsnCode: string | null;
  quantity: string;
  uom: string;
  unitRate: string;
  discountPercent: string;
  taxRate: string;
  freight: string;
  brandMake: string;
}

const RecordQuotationDialog: React.FC<{
  open: boolean;
  rfq: RfqDetail;
  onClose: () => void;
  onCreated: () => void;
}> = ({ open, rfq, onClose, onCreated }) => {
  const [vendorId, setVendorId] = useState<string>('');
  const [quotationDate, setQuotationDate] = useState(todayISO());
  const [validUntil, setValidUntil] = useState('');
  const [deliveryDays, setDeliveryDays] = useState('');
  const [qualityScore, setQualityScore] = useState('');
  const [technicalCompliance, setTechnicalCompliance] = useState<TechnicalCompliance>('COMPLIANT');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [warranty, setWarranty] = useState('');
  const [incoterms, setIncoterms] = useState('');
  const [freightAmount, setFreightAmount] = useState('0');
  const [insuranceAmount, setInsuranceAmount] = useState('0');
  const [otherCharges, setOtherCharges] = useState('0');
  const [discountAmount, setDiscountAmount] = useState('0');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<QuotationLineDraft[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setVendorId('');
    setQuotationDate(todayISO());
    setValidUntil('');
    setDeliveryDays('');
    setQualityScore('');
    setTechnicalCompliance('COMPLIANT');
    setPaymentTerms(rfq.payment_terms || '');
    setWarranty('');
    setIncoterms(rfq.incoterms || '');
    setFreightAmount('0');
    setInsuranceAmount('0');
    setOtherCharges('0');
    setDiscountAmount('0');
    setNotes('');
    setError(null);
    setLines(
      rfq.items.map((it) => ({
        key: `line-${it.id}`,
        rfqItemId: it.id,
        productId: it.product_id,
        itemDescription: it.product_name || '',
        hsnCode: it.hsn_code || null,
        quantity: String(toNum(it.quantity) || 1),
        uom: it.uom || '',
        unitRate: '',
        discountPercent: '0',
        taxRate: '18',
        freight: '0',
        brandMake: '',
      }))
    );
  }, [open, rfq]);

  const updateLine = (key: string, patch: Partial<QuotationLineDraft>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const estimatedSubtotal = useMemo(
    () => lines.reduce((sum, l) => sum + toNum(l.quantity) * toNum(l.unitRate) * (1 - toNum(l.discountPercent) / 100), 0),
    [lines]
  );

  const handleSubmit = async () => {
    setError(null);
    if (!vendorId) {
      setError('Select the vendor this quotation is from.');
      return;
    }
    if (lines.some((l) => toNum(l.unitRate) <= 0)) {
      setError('Every line needs a unit rate greater than zero.');
      return;
    }

    setSubmitting(true);
    try {
      await procurementService.createSupplierQuotation({
        rfqId: rfq.id,
        vendorId,
        quotationDate,
        validUntil: validUntil || undefined,
        deliveryDays: deliveryDays ? toNum(deliveryDays) : undefined,
        qualityScore: qualityScore ? toNum(qualityScore) : undefined,
        technicalCompliance,
        paymentTerms: paymentTerms.trim() || undefined,
        warranty: warranty.trim() || undefined,
        incoterms: incoterms.trim() || undefined,
        freightAmount: toNum(freightAmount),
        insuranceAmount: toNum(insuranceAmount),
        otherCharges: toNum(otherCharges),
        discountAmount: toNum(discountAmount),
        notes: notes.trim() || undefined,
        items: lines.map((l) => ({
          rfqItemId: l.rfqItemId,
          productId: l.productId || undefined,
          itemDescription: l.itemDescription.trim() || undefined,
          hsnCode: l.hsnCode || undefined,
          quantity: toNum(l.quantity),
          uom: l.uom.trim() || undefined,
          unitRate: toNum(l.unitRate),
          discountPercent: toNum(l.discountPercent),
          taxRate: toNum(l.taxRate),
          freight: toNum(l.freight),
          brandMake: l.brandMake.trim() || undefined,
        })),
      });
      onCreated();
    } catch (err: any) {
      setError(err.message || 'Failed to record quotation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <RequestQuote color="primary" />
        Record Supplier Quotation
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Vendor *"
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
            >
              <MenuItem value="" disabled>
                Select an invited supplier
              </MenuItem>
              {rfq.suppliers.map((s) => (
                <MenuItem key={s.vendor_id} value={s.vendor_id}>
                  {s.vendor_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Quotation Date"
              value={quotationDate}
              onChange={(e) => setQuotationDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Valid Until"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Delivery Days"
              value={deliveryDays}
              onChange={(e) => setDeliveryDays(e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Quality Score (0-10)"
              value={qualityScore}
              onChange={(e) => setQualityScore(e.target.value)}
              inputProps={{ min: 0, max: 10, step: '0.1' }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Technical Compliance"
              value={technicalCompliance}
              onChange={(e) => setTechnicalCompliance(e.target.value as TechnicalCompliance)}
            >
              <MenuItem value="COMPLIANT">Compliant</MenuItem>
              <MenuItem value="PARTIAL">Partial</MenuItem>
              <MenuItem value="NON_COMPLIANT">Non-Compliant</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Payment Terms"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField fullWidth size="small" label="Warranty" value={warranty} onChange={(e) => setWarranty(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField fullWidth size="small" label="Incoterms" value={incoterms} onChange={(e) => setIncoterms(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Freight (header)"
              value={freightAmount}
              onChange={(e) => setFreightAmount(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Insurance"
              value={insuranceAmount}
              onChange={(e) => setInsuranceAmount(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Other Charges"
              value={otherCharges}
              onChange={(e) => setOtherCharges(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Header Discount"
              value={discountAmount}
              onChange={(e) => setDiscountAmount(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Line Rates
        </Typography>
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Item</TableCell>
                <TableCell sx={headerCellSx} align="right">Qty</TableCell>
                <TableCell sx={headerCellSx} align="right">Unit Rate</TableCell>
                <TableCell sx={headerCellSx} align="right">Disc %</TableCell>
                <TableCell sx={headerCellSx} align="right">Tax %</TableCell>
                <TableCell sx={headerCellSx} align="right">Freight</TableCell>
                <TableCell sx={headerCellSx}>Brand / Make</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l) => (
                <TableRow key={l.key} sx={bodyRowSx}>
                  <TableCell sx={{ py: 1 }}>{l.itemDescription || '—'}</TableCell>
                  <TableCell align="right" sx={{ minWidth: 80 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={l.quantity}
                      onChange={(e) => updateLine(l.key, { quantity: e.target.value })}
                      inputProps={{ min: 0, step: '0.01' }}
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 100 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={l.unitRate}
                      onChange={(e) => updateLine(l.key, { unitRate: e.target.value })}
                      inputProps={{ min: 0, step: '0.01' }}
                      sx={{ minWidth: 100 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 80 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={l.discountPercent}
                      onChange={(e) => updateLine(l.key, { discountPercent: e.target.value })}
                      inputProps={{ min: 0, max: 100, step: '0.01' }}
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 80 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={l.taxRate}
                      onChange={(e) => updateLine(l.key, { taxRate: e.target.value })}
                      inputProps={{ min: 0, step: '0.01' }}
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 90 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={l.freight}
                      onChange={(e) => updateLine(l.key, { freight: e.target.value })}
                      inputProps={{ min: 0, step: '0.01' }}
                      sx={{ minWidth: 90 }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    <TextField
                      size="small"
                      value={l.brandMake}
                      onChange={(e) => updateLine(l.key, { brandMake: e.target.value })}
                      sx={{ minWidth: 130 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Estimated subtotal (before header charges/tax): <strong>{formatIndianNumber(estimatedSubtotal)}</strong>
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Recording...' : 'Record Quotation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Award dialog
// ---------------------------------------------------------------------------

const AwardDialog: React.FC<{
  open: boolean;
  rfq: RfqDetail;
  eligibleQuotations: RfqDetail['quotations'];
  defaultQuotationId: number | null;
  onClose: () => void;
  onAwarded: (purchaseOrderId: number | null) => void;
}> = ({ open, rfq, eligibleQuotations, defaultQuotationId, onClose, onAwarded }) => {
  const [quotationId, setQuotationId] = useState<number | ''>('');
  const [justification, setJustification] = useState('');
  const [createPo, setCreatePo] = useState(true);
  const [poDate, setPoDate] = useState(todayISO());
  const [deliveryDate, setDeliveryDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuotationId(defaultQuotationId && eligibleQuotations.some((q) => q.id === defaultQuotationId) ? defaultQuotationId : eligibleQuotations[0]?.id ?? '');
    setJustification('');
    setCreatePo(true);
    setPoDate(todayISO());
    setDeliveryDate('');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultQuotationId]);

  const handleSubmit = async () => {
    if (!quotationId) {
      setError('Select the quotation to award.');
      return;
    }
    if (!justification.trim()) {
      setError('A justification is required to award an RFQ.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await procurementService.awardRfq(rfq.id, {
        quotationId: quotationId as number,
        justification: justification.trim(),
        createPo,
        poDate: createPo ? poDate : undefined,
        deliveryDate: createPo && deliveryDate ? deliveryDate : undefined,
      });
      onAwarded(res.purchaseOrderId);
    } catch (err: any) {
      setError(err.message || 'Failed to award RFQ');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EmojiEvents color="success" />
        Award RFQ
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {eligibleQuotations.length === 0 ? (
          <Alert severity="warning">No eligible quotations to award. Record or shortlist a quotation first.</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              select
              fullWidth
              label="Winning Quotation *"
              value={quotationId}
              onChange={(e) => setQuotationId(Number(e.target.value))}
            >
              {eligibleQuotations.map((q) => (
                <MenuItem key={q.id} value={q.id}>
                  {q.vendor_name} — {formatIndianNumber(toNum(q.landed_cost ?? q.total_amount))}
                  {q.delivery_days !== null ? ` · ${toNum(q.delivery_days)}d` : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              required
              multiline
              minRows={3}
              label="Justification (required)"
              placeholder="Why was this quotation selected?"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
            />
            <FormControlLabel
              control={<Checkbox checked={createPo} onChange={(e) => setCreatePo(e.target.checked)} />}
              label="Create a purchase order now"
            />
            {createPo && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="PO Date"
                  type="date"
                  value={poDate}
                  onChange={(e) => setPoDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, minWidth: 160 }}
                />
                <TextField
                  label="Delivery Date"
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, minWidth: 160 }}
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={submitting || eligibleQuotations.length === 0}
        >
          {submitting ? 'Awarding...' : 'Award RFQ'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RfqDetailPage;
